import React, { useEffect, useRef, useState, useCallback } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, RefreshCw, AlertTriangle } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
// Iter-300 "Break-the-Glass" Part 2 — every check-in QR mint / poll
// call routes through `CheckInEngine` so the token API surface stays
// centralised. See SYSTEM_RULES.md (Check-In Engine Isolation).
import { mintCheckinQrToken, fetchCustomerCheckinState } from "@/lib/CheckInEngine";

/**
 * QR-Only Front-Desk Check-In (Feb 2026).
 *
 * The picker no longer offers Email / WhatsApp / Tablet channels — the
 * moment the operator clicks "Check-In Guest" we mint a single-use QR
 * token and render the QR code centered on the dialog. The QR link
 * routes to /portal/checkin where a Smart Medical Screening branch
 * decides between the classic 2-step new-guest wizard OR the new
 * Medical Review page (returning guests, pre-filled from history).
 *
 * The dialog polls the customer's `checked_in_at` timestamp every 4s
 * so it auto-closes and toasts the front desk the instant the guest
 * finishes on their phone. That timestamp cascade also flips the
 * Booking-Calendar tile teal ("Checked-In").
 */
export default function CheckInDialog({ open, onOpenChange, customer, onSent, onCompleted }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // { link, expires_at }
  const [error, setError] = useState(null);
  // Lifetime no-show tally for the guest — fetched lazily on open so
  // the dialog can flash a subtle warning strip before the operator
  // hands over the QR. Never blocks the check-in flow; treats the
  // fetch as best-effort.
  const [noShowInfo, setNoShowInfo] = useState(null); // { count, last_no_show_at }
  const openedAtRef = useRef(null);

  const cid = customer?.id;
  const first = customer?.first_name || (customer?.full_name || "").split(" ")[0] || "there";

  // Auto-mint the QR token the moment the dialog opens so the operator
  // never has to press a "Generate" button — the QR is on-screen before
  // they can hand the phone over. Extracted to a callback so the
  // "Regenerate QR" button can re-invoke it on demand.
  const mintQr = useCallback(async () => {
    if (!cid) return;
    setBusy(true);
    setError(null);
    try {
      const data = await mintCheckinQrToken(cid);
      setResult(data);
      onSent && onSent(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }, [cid, onSent]);

  useEffect(() => {
    if (!open) {
      setResult(null); setError(null); openedAtRef.current = null;
      setNoShowInfo(null);
      return;
    }
    openedAtRef.current = new Date().toISOString();
    if (!cid) return;
    let cancelled = false;
    (async () => {
      await mintQr();
      if (cancelled) return;
      // Pull no-show tally alongside the QR mint. Best-effort — an
      // error here should never block the check-in flow.
      try {
        const r = await staffApi.get(`/customers/${cid}`);
        const s = r.data?.summary || {};
        if (!cancelled) setNoShowInfo({
          count: Number(s.no_show_count || 0),
          last_no_show_at: s.last_no_show_at || null,
        });
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open, cid, mintQr]);

  // Poll the guest's `checked_in_at` — auto-close + toast the moment
  // they finish so the front desk doesn't need to babysit the modal.
  useEffect(() => {
    if (!open || !result?.link || !cid) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const c = await fetchCustomerCheckinState(cid);
        const cin = c.checked_in_at;
        if (cin && cin > openedAtRef.current) {
          if (cancelled) return;
          toast.success(`${first} just completed check-in.`);
          try {
            window.dispatchEvent(new CustomEvent("cocoon:customer-updated", {
              detail: { customer_id: cid, checked_in_at: cin, customer: c },
            }));
          } catch {}
          onCompleted && onCompleted(c);
          onSent && onSent({ ...result, customer: c });
          onOpenChange && onOpenChange(false);
        }
      } catch {}
    };
    const id = setInterval(tick, 4000);
    tick();
    return () => { cancelled = true; clearInterval(id); };
  }, [open, result, cid, first, onCompleted, onSent, onOpenChange]);

  const copyLink = async () => {
    if (!result?.link) return;
    try {
      await navigator.clipboard.writeText(result.link);
      toast.success("Link copied to clipboard.");
    } catch {
      toast.error("Couldn't access clipboard — long-press the link to copy.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-2rem)] max-w-sm p-4 flex flex-col"
        data-testid="checkin-dialog"
      >
        <DialogHeader className="space-y-1 text-center sm:text-center">
          <DialogTitle className="text-base">Check-in {first}</DialogTitle>
          <DialogDescription className="text-[11px] leading-snug">
            Ask the guest to point their phone camera at this QR. It opens their secure check-in on this device or theirs.
          </DialogDescription>
        </DialogHeader>

        {/* Repeat No-Show warning strip. Rendered only when the guest
            has ≥1 lifetime no-show so a clean-record guest sees a
            spotless dialog. Non-blocking — the front desk can proceed
            with check-in regardless. */}
        {noShowInfo && noShowInfo.count > 0 && (
          <div
            className="mt-3 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5"
            data-testid="checkin-noshow-warning"
          >
            <AlertTriangle className="w-3.5 h-3.5 text-rose-600 mt-0.5 flex-shrink-0" />
            <div className="text-[10px] leading-tight text-rose-800">
              <div className="font-semibold" data-testid="checkin-noshow-count">
                {noShowInfo.count} no-show{noShowInfo.count === 1 ? "" : "s"} on record
              </div>
              {noShowInfo.last_no_show_at && (
                <div className="text-rose-700/80">
                  Last incident: {new Date(noShowInfo.last_no_show_at).toLocaleDateString("en-GB", { timeZone: "Asia/Makassar", day: "2-digit", month: "short", year: "numeric" })}
                </div>
              )}
            </div>
          </div>
        )}

        <div
          className="mt-3 w-full flex flex-col items-center justify-center gap-3"
          data-testid="checkin-qr-card"
        >
          {busy && !result?.link && (
            <p className="text-xs text-muted-foreground" data-testid="checkin-qr-loading">Generating QR…</p>
          )}
          {error && (
            <div className="w-full text-center space-y-2">
              <p className="text-xs text-red-600" data-testid="checkin-qr-error">{error}</p>
              <Button size="sm" onClick={mintQr} disabled={busy} className="rounded-full text-[11px]" data-testid="checkin-qr-retry">
                <RefreshCw className="w-3 h-3 mr-1" /> Try again
              </Button>
            </div>
          )}
          {result?.link && (
            <>
              {/* Centered, responsive QR: outer wrap uses Flexbox to
                  perfectly center the code, width is capped at 80% of
                  the popup (never touches the edges on iPad / phone in
                  any orientation) with a 296-px ceiling. Inner SVG
                  scales to 100% of the wrap via CSS width/height so
                  the numeric `size={256}` becomes a viewBox only — the
                  rendered pixels adapt to the container. 20-px solid
                  white padding is the mandatory quiet zone.
                  `shapeRendering="crispEdges"` disables anti-aliasing
                  on Retina so camera autofocus locks instantly. */}
              <div
                className="rounded-md border border-border shadow-sm flex items-center justify-center"
                style={{
                  width: "min(80%, 296px)",
                  aspectRatio: "1 / 1",
                  padding: 20,
                  backgroundColor: "#FFFFFF",
                  zIndex: 60,
                  isolation: "isolate",
                }}
                data-testid="checkin-qr-svg-wrap"
              >
                <QRCodeSVG
                  key={result.link}
                  value={result.link}
                  size={256}
                  marginSize={0}
                  level="M"
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                  shapeRendering="crispEdges"
                  style={{ display: "block", width: "100%", height: "100%" }}
                  data-testid="checkin-qr-svg"
                />
              </div>
              <p className="text-[10px] text-muted-foreground italic text-center" data-testid="checkin-qr-waiting">
                Waiting for {first} to finish on their phone…
              </p>
              <Button
                size="sm"
                variant="ghost"
                onClick={mintQr}
                disabled={busy}
                className="h-7 px-2 text-[10px] gap-1"
                data-testid="checkin-qr-regenerate"
              >
                <RefreshCw className={"w-3 h-3 " + (busy ? "animate-spin" : "")} />
                {busy ? "Regenerating…" : "Regenerate QR"}
              </Button>
            </>
          )}
        </div>

        {result?.link && (
          <div className="flex items-center justify-between gap-2 border-t border-border pt-2 mt-3">
            <p className="text-[10px] text-muted-foreground truncate flex-1" title={result.link} data-testid="checkin-link-preview">{result.link}</p>
            <Button size="sm" variant="ghost" onClick={copyLink} className="h-6 px-2 text-[10px]" data-testid="checkin-copy-link"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
